/**
 * draftHandoff.ts
 *
 * In-memory hand-off from a draft Workspace to the real Workspace it creates.
 *
 * A DD/BA draft creates its job on the first real action and then replaces the
 * URL with the job's Intake. Whatever the user already gave the draft (a parsed
 * loan tape, files that finished uploading) is parked here under the new job id
 * so the real Intake can pick it up and show it at once, instead of the user
 * having to repeat the step. The data is read with `peek` and removed with
 * `clear` so a React re-render or a Strict Mode double effect cannot lose it.
 *
 * Entered BA form values do not go through here: they are saved to the loan and
 * to the per-job intake record before the URL changes.
 *
 * No React and no I/O.
 */

import type { Tape } from './intake/intakeDDModel';

/** A file the draft already uploaded to the new job, shown as "Ready" in BA Intake. */
export interface DraftStagedFile {
  id: string;
  /** Name the file is stored under. */
  name: string;
  size: number;
  status: 'completed';
}

const tapes = new Map<string, Tape>();
const stagedFiles = new Map<string, DraftStagedFile[]>();

export function setDraftTape(jobId: string, tape: Tape): void {
  tapes.set(jobId, tape);
}

export function peekDraftTape(jobId: string): Tape | null {
  return tapes.get(jobId) ?? null;
}

export function clearDraftTape(jobId: string): void {
  tapes.delete(jobId);
}

export function setDraftStagedFiles(jobId: string, files: ReadonlyArray<DraftStagedFile>): void {
  if (files.length > 0) stagedFiles.set(jobId, [...files]);
}

export function peekDraftStagedFiles(jobId: string): DraftStagedFile[] {
  return stagedFiles.get(jobId) ?? [];
}

export function clearDraftStagedFiles(jobId: string): void {
  stagedFiles.delete(jobId);
}
